'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/contexts/auth-context'
import { toast } from 'sonner'
import { validateEmail } from '@/lib/validation'

interface UseBetaAuthOptions {
  redirectTo?: string
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function useBetaAuth(options: UseBetaAuthOptions = {}) {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { signIn } = useAuth()

  const redirectTo = options.redirectTo || '/dashboard'

  const handleBetaLogin = async (email: string, password: string) => {
    console.log('🧪 Beta login attempt for:', email)
    
    // Basic email validation
    const validation = validateEmail(email)
    if (!validation.isValid) {
      const errorMessage = validation.errors.join(', ')
      console.log('❌ Beta login validation failed:', errorMessage)
      toast.error(errorMessage)
      options.onError?.(errorMessage)
      return
    }

    // Check for hardcoded beta password
    if (password !== 'socialloop2024!') {
      const errorMessage = 'Invalid beta password'
      console.log('❌ Beta password incorrect')
      toast.error(errorMessage)
      options.onError?.(errorMessage)
      return
    }

    console.log('✅ Beta validation passed, calling backend...')
    setLoading(true)
    try {
      // Use beta signin (isBeta = true)
      const result = await signIn(email, password, true)
      console.log('🔍 Beta login result:', result)
      
      if (result.success) {
        console.log('✅ Beta login successful, redirecting to:', redirectTo)
        toast.success('Welcome to the SocialLoop beta!')
        options.onSuccess?.()
        router.push(redirectTo)
      } else {
        const errorMessage = result.error || 'Beta login failed'
        console.log('❌ Beta login failed:', errorMessage)
        toast.error(errorMessage)
        options.onError?.(errorMessage)
      }
    } catch (error) {
      const errorMessage = 'An unexpected error occurred'
      console.log('❌ Beta login error:', error)
      toast.error(errorMessage)
      options.onError?.(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    handleBetaLogin,
  }
}
